<template>
  <v-container fluid>
    <v-card outlined :elevation="2" :loading="loading" style="width: 100%">
      <v-card-title>
        <v-img
          :src="image"
          class="mx-2"
          max-height="40"
          max-width="100"
          contain
        />
        <v-spacer />
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Search"
          single-line
          hide-details
          @keyup="onSearchInput"
        />
      </v-card-title>
      <v-divider />

      <v-data-table
        :headers="headers"
        :items="items"
        :items-per-page="itemsPerPage"
        :page="currentPage"
        :items-per-page-options="[10, 12, 15, 20]"
        :server-items-length="pagination.total"
        :multi-sort="true"
        :footer-props="{
          itemsPerPageText: 'Rows per page:',
          'items-per-page-options': [10, 12, 15, 20],
          pageText: footerText
        }"
        @update:page="onPageChange"
        @update:items-per-page="onLimitChange"
        @click:row="onRowClick"
      >
        <template v-slot:no-data>
          <v-container>
            <v-row justify="center">
              <v-col cols="auto">
                <span class="text-center">No matching records found</span>
              </v-col>
            </v-row>
          </v-container>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import { dateFormat, toEpochTimestamp } from '../plugins/date';

export default {
  name: 'device-list-data-table',
  props: {
    deviceType: {
      type: String,
      required: true
    },
    devices: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      required: true
    },
    pagination: {
      type: Object,
      default: () => ({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
      })
    }
  },
  data() {
    return {
      search: '',
      debounceTimer: null,
      currentPage: 1,
      itemsPerPage: 10,
      headers: [
        {
          text: 'Serial No.',
          value: 'topic',
          sortable: true
        },
        {
          text: 'Created On',
          value: 'createdAt',
          sortable: false
        },
        {
          text: 'Last Updated On',
          value: 'updatedAt',
          sortable: true,
          sort: (a, b) => toEpochTimestamp(a) - toEpochTimestamp(b)
        }
      ]
    };
  },
  computed: {
    items() {
      return this.devices
        .map(device => {
          return {
            topic: device.Topic,
            createdAt: dateFormat(device.createdAt),
            updatedAt: dateFormat(device.updatedAt)
          };
        })
        .sort((a, b) => {
          return toEpochTimestamp(b.updatedAt) - toEpochTimestamp(a.updatedAt);
        });
    },
    image() {
      if (this.deviceType === 'ecotron') {
        return 'https://api.ecozen.ai/dashboard/assets/images/logos/ecotron.png';
      } else if (this.deviceType === 'ecofrost') {
        return 'https://api.ecozen.ai/dashboard/assets/images/logos/ecofrost.png';
      } else {
        return 'https://api.ecozen.ai/dashboard/assets/images/logos/ecozen_white.png';
      }
    },
    startRecord() {
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },
    endRecord() {
      return Math.min(
        this.currentPage * this.itemsPerPage,
        this.pagination.total
      );
    },
    footerText() {
      return `${this.startRecord}-${this.endRecord} of ${this.pagination.total} | Page: ${this.currentPage} | Total Pages: ${this.pagination.totalPages}`;
    }
  },
  watch: {
    'pagination.page': function (newPage) {
      this.currentPage = newPage;
    },
    'pagination.limit': function (newLimit) {
      this.itemsPerPage = newLimit;
    }
  },
  methods: {
    onSearchInput() {
      clearTimeout(this.debounceTimer);

      this.debounceTimer = setTimeout(() => {
        if (this.search.length >= 5) {
          this.$emit('search', this.search);
        } else if (this.search.length === 0) {
          this.$emit('search', '');
        }
      }, 500);
    },
    onPageChange(newPage) {
      this.currentPage = newPage;
      if (newPage !== this.pagination.page) {
        this.$emit('page-changed', newPage);
      }
    },
    onLimitChange(newLimit) {
      this.itemsPerPage = newLimit;
      if (newLimit !== this.pagination.limit) {
        this.$emit('limit-changed', newLimit);
      }
    },
    onRowClick(device) {
      this.$router.push(`/${this.deviceType}/${device.topic}`);
    }
  },
  mounted() {
    this.currentPage = this.pagination.page || 1;
    this.itemsPerPage = this.pagination.limit || 10;
  },
  beforeDestroy() {
    clearTimeout(this.debounceTimer);
  }
};
</script>
